import { CompanyOffice } from 'src/app/classes/models/company-office';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { ESignProvider } from 'src/app/classes/models/esign-provider';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { CompanyServiceRanking } from 'src/app/interfaces/models/company-service-ranking';
import { RegistrationProgress } from 'src/app/interfaces/models/registration-progress';

import { Address } from './address';
import { ApConfig } from './ap-config';
import { BaseModel } from './base-model';
import { BusinessComplianceSetting } from './business-compliance-setting';
import { ClientRegisteredVendorFee } from './client-registered-vendor-fee';
import { CompanyComplianceType } from './company-compliance-type';
import { CompanyFee } from './company-fee';
import { CompanyOperationState } from './company-operation-state';
import { CompanyPackagePricing } from './company-package-pricing';
import { CompanyRanking } from './company-ranking';
import { CompanyVendor } from './company-vendor';
import { Contact } from './contact';
import { Contract } from './contract';
import { File as ViveFile } from './file';
import { Site } from './site';

export class Company extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static CompanyProperties: (
    | {
        property: keyof Company;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Company;
        type: 'alias';
      }
    | {
        property: keyof Company;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'structure', type: 'column', dataType: 'string' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'ein', type: 'column', dataType: 'string' },
    { property: 'country', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'fax', type: 'column', dataType: 'string' },
    { property: 'website', type: 'column', dataType: 'string' },
    { property: 'logo', type: 'column', dataType: 'string' },
    { property: 'LogoFileId', type: 'column', dataType: 'number' },
    { property: 'facebook', type: 'column', dataType: 'string' },
    { property: 'twitter', type: 'column', dataType: 'string' },
    { property: 'linkedIn', type: 'column', dataType: 'string' },
    { property: 'mailingAddress1', type: 'column', dataType: 'string' },
    { property: 'mailingAddress2', type: 'column', dataType: 'string' },
    { property: 'mailingCity', type: 'column', dataType: 'string' },
    { property: 'mailingState', type: 'column', dataType: 'string' },
    { property: 'mailingZip', type: 'column', dataType: 'string' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    {
      property: 'listApprovedVendorsInPortal',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'launchWindow', type: 'column', dataType: 'number' },
    { property: 'launchStartDate', type: 'column', dataType: 'date' },
    {
      property: 'allowVendorsToRequestJoin',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'hasGlobalCompliance', type: 'column', dataType: 'boolean' },
    { property: 'fullServiceOnsite', type: 'column', dataType: 'boolean' },
    { property: 'fullServiceOffsite', type: 'column', dataType: 'boolean' },
    { property: 'premiumServiceOnsite', type: 'column', dataType: 'boolean' },
    { property: 'readyForVive', type: 'column', dataType: 'boolean' },
    { property: 'companyType', type: 'column', dataType: 'string' },
    { property: 'registrationStatus', type: 'column', dataType: 'string' },
    { property: 'descriptionOfOperations', type: 'column', dataType: 'string' },
    { property: 'contractPDF', type: 'column', dataType: 'string' },
    { property: 'sampleCert', type: 'column', dataType: 'string' },
    { property: 'waiverPolicyUrl', type: 'column', dataType: 'string' },
    { property: 'exibitA', type: 'column', dataType: 'string' },
    { property: 'contractAdobeSign', type: 'column', dataType: 'string' },
    { property: 'vendorSignUpCode', type: 'column', dataType: 'string' },
    { property: 'feeWaiverAllowedAmt', type: 'column', dataType: 'number' },
    { property: 'clientPaid', type: 'column', dataType: 'boolean' },
    { property: 'packageType', type: 'column', dataType: 'string' },
    { property: 'clientVendorContact', type: 'column', dataType: 'string' },
    { property: 'viveChampionContact', type: 'column', dataType: 'string' },
    { property: 'vendorAgreementContact', type: 'column', dataType: 'string' },
    { property: 'waiverContact', type: 'column', dataType: 'string' },
    { property: 'clientVendorPrice', type: 'column', dataType: 'string' },
    { property: 'vendorPriceNotes', type: 'column', dataType: 'string' },
    {
      property: 'vendorRegistrationPageUrl',
      type: 'column',
      dataType: 'string'
    },
    { property: 'acceptsWaivers', type: 'column', dataType: 'boolean' },
    {
      property: 'acceptsAgreementRevisions',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'agreementRevisionNotes',
      type: 'column',
      dataType: 'string'
    },
    { property: 'generalClientNotes', type: 'column', dataType: 'string' },
    {
      property: 'usesThirdPartyBillPay',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'thirdPartyBillPayProvider',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'thirdPartyBillPayNotes',
      type: 'column',
      dataType: 'string'
    },
    { property: 'hasVetProfessionals', type: 'column', dataType: 'boolean' },
    { property: 'pmtNotes', type: 'column', dataType: 'string' },
    { property: 'allowExemptVendors', type: 'column', dataType: 'boolean' },
    {
      property: 'isExemptVendorTypesSetup',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'lastDateRanAllFileDownload',
      type: 'column',
      dataType: 'date'
    },
    {
      property: 'allowClientRegisteredVendors',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'allowAutomatedBusinessVerificationClientRegisteredVendors',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'verifyClientRegisteredVendorBusinessOnly',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'creditClientRegisteredVendor',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'allowTinCheckOnlyFee', type: 'column', dataType: 'boolean' },
    { property: 'useTransporterTrades', type: 'column', dataType: 'boolean' },
    { property: 'allowTollBrothers', type: 'column', dataType: 'boolean' },
    { property: 'isAcceptingNewVendors', type: 'column', dataType: 'boolean' },
    {
      property: 'allowClientRegisteredVendorEmails',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'allowAdditionalInsuredEndorsements',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'crvEmailInvitationSendDate',
      type: 'column',
      dataType: 'date'
    },
    { property: 'ESignProviderId', type: 'column', dataType: 'number' },
    { property: 'allowIRSTinMatchFee', type: 'column', dataType: 'boolean' },
    {
      property: 'allowCorporateFilingReportFee',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'globalAssignSites', type: 'column', dataType: 'boolean' },

    { property: 'package', type: 'alias' },
    { property: 'allOperationStates', type: 'alias' },
    { property: 'feeVouchersLeft', type: 'alias' },
    { property: 'totalVendorsFeeWaived', type: 'alias' },
    { property: 'hasApConfig', type: 'alias' },

    { property: 'CompanyFees', model: 'CompanyFee', type: 'association' },
    { property: 'CompanyVendors', model: 'CompanyVendor', type: 'association' },
    { property: 'CompanyOffices', model: 'CompanyOffice', type: 'association' },
    {
      property: 'ComplianceSettings',
      model: 'ComplianceSetting',
      type: 'association'
    },
    {
      property: 'RegistrationProgresses',
      model: 'RegistrationProgress',
      type: 'association'
    },
    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    {
      property: 'MailingAddress',
      model: 'MailingAddress',
      type: 'association'
    },
    { property: 'Contacts', model: 'Contact', type: 'association' },
    {
      property: 'CompanyPackagePricing',
      model: 'CompanyPackagePricing',
      type: 'association'
    },
    { property: 'ApConfigs', model: 'ApConfig', type: 'association' },
    {
      property: 'CompanyServiceRankings',
      model: 'CompanyServiceRanking',
      type: 'association'
    },
    {
      property: 'CompanyOperationStates',
      model: 'CompanyOperationState',
      type: 'association'
    },
    {
      property: 'CompanyComplianceTypes',
      model: 'CompanyComplianceType',
      type: 'association'
    },
    { property: 'Sites', model: 'Site', type: 'association' },
    { property: 'Contracts', model: 'Contract', type: 'association' },
    {
      property: 'ClientRegisteredVendorFees',
      model: 'ClientRegisteredVendorFee',
      type: 'association'
    },
    { property: 'ESignProvider', model: 'ESignProvider', type: 'association' },
    { property: 'LogoFile', model: 'ViveFile', type: 'association' }
  ];

  static {
    Company.CompanyProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Company.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Company.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Company.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(CompanyObject: Partial<Company>) {
    super();

    Company.CompanyProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = CompanyObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  legalName?: string | null;
  dba?: string | null;
  structure?: string | null;
  ssn?: string | null;
  ein?: string | null;
  country?: 'US' | 'CA';
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
  logo?: string | null;
  LogoFileId?: number | null;
  facebook?: string | null;
  twitter?: string | null;
  linkedIn?: string | null;
  mailingAddress1?: string | null;
  mailingAddress2?: string | null;
  mailingCity?: string | null;
  mailingState?: string | null;
  mailingZip?: string | null;
  status?: 'unactivated' | 'reviewed' | 'activated' | 'deactivated';
  statusDate?: Date | null;
  listApprovedVendorsInPortal?: boolean;
  launchWindow?: number | null;
  launchStartDate?: Date | null;
  allowVendorsToRequestJoin?: boolean;
  hasGlobalCompliance?: boolean;
  fullServiceOnsite?: boolean;
  fullServiceOffsite?: boolean;
  premiumServiceOnsite?: boolean;
  readyForVive?: boolean;
  companyType?: string | null;
  registrationStatus?: 'unconfirmed' | 'confirmed' | 'complete';
  descriptionOfOperations?: string | null;
  contractPDF?: string | null;
  sampleCert?: string | null;
  waiverPolicyUrl?: string | null;
  exibitA?: string | null;
  contractAdobeSign?: string | null;
  vendorSignUpCode?: string | null;
  feeWaiverAllowedAmt?: number | null;
  clientPaid?: boolean;
  packageType?: 'BASE' | 'FULL';
  clientVendorContact?: string | null;
  viveChampionContact?: string | null;
  vendorAgreementContact?: string | null;
  waiverContact?: string | null;
  clientVendorPrice?: string | null;
  vendorPriceNotes?: string | null;
  vendorRegistrationPageUrl?: string | null;
  acceptsWaivers?: boolean | null;
  acceptsAgreementRevisions?: boolean | null;
  agreementRevisionNotes?: string | null;
  generalClientNotes?: string | null;
  usesThirdPartyBillPay?: boolean | null;
  thirdPartyBillPayProvider?: string | null;
  thirdPartyBillPayNotes?: string | null;
  hasVetProfessionals?: boolean | null;
  pmtNotes?: string | null;
  allowExemptVendors?: boolean;
  isExemptVendorTypesSetup?: boolean;
  lastDateRanAllFileDownload?: Date | null;
  allowClientRegisteredVendors?: boolean;
  allowAutomatedBusinessVerificationClientRegisteredVendors?: boolean;
  usesNewBusinessVerification?: boolean;
  verifyClientRegisteredVendorBusinessOnly?: boolean;
  creditClientRegisteredVendor?: boolean;
  allowTinCheckOnlyFee?: boolean;
  useTransporterTrades?: boolean;
  allowTollBrothers?: boolean;
  isAcceptingNewVendors?: boolean;
  allowClientRegisteredVendorEmails?: boolean;
  allowAdditionalInsuredEndorsements?: boolean;
  crvEmailInvitationSendDate?: Date;
  ESignProviderId?: number;
  allowIRSTinMatchFee?: boolean;
  allowCorporateFilingReportFee?: boolean;
  globalAssignSites?: boolean;

  // Alias columns
  // This column is similar to 'packageType' above. However, packageType is not set properly
  // for Client registration, thus we currently calculate it using 'fullServiceOnsite' & 'fullServiceOffsite'
  package?: 'BASE' | 'FULL';
  allOperationStates?: string;
  feeVouchersLeft?: number;
  totalVendorsFeeWaived?: number;
  hasApConfig?: boolean;

  ApConfigs?: ApConfig[];
  CompanyFees?: CompanyFee[];
  CompanyVendors?: CompanyVendor[];
  CompanyOffices?: CompanyOffice[];
  CompanyRankings?: CompanyRanking[];
  ComplianceSettings?: ComplianceSetting[];
  BusinessComplianceSettings?: BusinessComplianceSetting[];
  RegistrationProgresses?: RegistrationProgress[];
  Addresses?: Address[];
  StreetAddress?: Address;
  MailingAddress?: Address;
  Contacts?: Contact[];
  CompanyComplianceTypes?: CompanyComplianceType[];
  CompanyOperationStates?: CompanyOperationState[];
  CompanyPackagePricing?: CompanyPackagePricing;
  CompanyServiceRankings?: CompanyServiceRanking[];
  Sites?: Site[];
  Contracts?: Contract[];
  ClientRegisteredVendorFees?: ClientRegisteredVendorFee[];
  ESignProvider?: ESignProvider;
  LogoFile?: ViveFile;
}
